import React, { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux';
import styles from './ModuleFilterRecords.module.css'
import { useTranslation } from "react-i18next";
import ModuleSingleRecordSections from './ModuleSingleRecordSections';
import CrmButton from '../../../UI/Crm/CrmButton/CrmButton';
import CrmDivider from '../../../UI/Crm/CrmLayout/CrmDivider';
import { Box } from '@mui/material';
import {
    getModuleLastFilters,
    saveModuleFilters,
    clearModuleFilters
} from '../../../../axios/requests/moduleFilterReq';   // adjust relative path if needed

const ModuleFilterRecords = ({ onCancelClicked, moduleObj, onApplyFilters, onSelectConv, OnAddToDialListClicked, useLocalFilters = false }) => {
    const [moduleSections, setModuleSections] = useState([])
    const [fieldValues, setFieldValues] = useState({})
    const [t] = useTranslation('crm')
    const crmState = useSelector(state => state.crm)
    // const { selectedCrmModule } = crmState
    let selectedCrmModule
    if (moduleObj) {
        selectedCrmModule = moduleObj
    }
    else {
        selectedCrmModule = crmState?.selectedCrmModule
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target
        setFieldValues((prev) => {
            const newFieldsState = JSON.parse(JSON.stringify(prev))
            newFieldsState[name] = type === 'checkbox' ? checked : value

            // Handle cascading dropdown reset logic
            if (name && selectedCrmModule?.sections) {
                // Check if this is a cascading dropdown field or child field
                const isChildField = name.includes('_child_');
                const baseFieldName = isChildField ? name.split('_child_')[0] : name;

                // Find the field definition
                const fieldDef = selectedCrmModule.sections
                    ?.flatMap(section => section.fields || [])
                    ?.find(field => field.name === baseFieldName);

                // Check if this field has cascading children
                const hasCascadingChildren = fieldDef?.type === 'select' &&
                    fieldDef.dropDownId?.dropDownItems?.some(item => item.children && item.children.length > 0);

                if (hasCascadingChildren) {
                    if (isChildField) {
                        // This is a child field change - reset all deeper child levels
                        const currentLevel = parseInt(name.split('_child_')[1]);

                        // Reset all child fields with higher levels
                        Object.keys(newFieldsState).forEach(fieldName => {
                            if (fieldName.startsWith(baseFieldName + '_child_')) {
                                const level = parseInt(fieldName.split('_child_')[1]);
                                if (level > currentLevel) {
                                    delete newFieldsState[fieldName];
                                }
                            }
                        });
                    } else {
                        // This is a parent field change - reset all child fields
                        Object.keys(newFieldsState).forEach(fieldName => {
                            if (fieldName.startsWith(baseFieldName + '_child_')) {
                                delete newFieldsState[fieldName];
                            }
                        });
                    }
                }
            }

            return newFieldsState
        })
    }

    // Loading of module sections
    useEffect(() => {
        if (selectedCrmModule?.sections?.length) {
            const orderedSection = JSON.parse(JSON.stringify(selectedCrmModule.sections))
                .filter((section) => section.order && section.name !== 'systemSection')
                .sort((sectionA, sectionB) => sectionA.order - sectionB.order)
            orderedSection.forEach(section => {
                const orderedSectionFields = [...section.fields].filter((field) => field.order && field.showFilters).sort((fieldA, fieldB) => fieldA.order - fieldB.order)
                section.fields = orderedSectionFields
            })
            setModuleSections(orderedSection.filter((section) => section.fields.length))
        }
    }, [selectedCrmModule?.sections])

    useEffect(() => {
        const fetchLastFilters = async () => {
            if (!selectedCrmModule?._id) return;

            // Skip backend fetch if using local filters only
            if (useLocalFilters) return;

            const lastFilters = await getModuleLastFilters(selectedCrmModule._id);

            if (lastFilters.length) {
                const savedValues = {};
                
                // Group filters by field name to handle date ranges
                const filterGroups = {};
                lastFilters.forEach(f => {
                    if (!filterGroups[f.field]) {
                        filterGroups[f.field] = [];
                    }
                    filterGroups[f.field].push(f);
                });
                
                // Process each field's filters
                Object.keys(filterGroups).forEach(fieldName => {
                    const fieldFilters = filterGroups[fieldName];

                    if (fieldFilters.length === 1) {
                        const filter = fieldFilters[0];

                        // Check if this is a cascading dropdown filter
                        if (filter.isCascadingDropdown && filter.cascadeValues) {
                            console.log('Restoring cascading dropdown filter:', filter);

                            // Restore parent values
                            if (filter.cascadeValues.parent) {
                                savedValues[fieldName] = filter.cascadeValues.parent;
                            }

                            // Restore child level values
                            Object.keys(filter.cascadeValues).forEach(key => {
                                if (key.startsWith('level_')) {
                                    const level = key.split('_')[1];
                                    const childFieldName = `${fieldName}_child_${level}`;
                                    savedValues[childFieldName] = filter.cascadeValues[key];
                                }
                            });
                        }
                        // Check if this is a number filter with operator
                        else {
                            const fieldDef = selectedCrmModule?.sections
                                ?.flatMap(section => section.fields || [])
                                ?.find(field => field.name === fieldName);

                            if (fieldDef?.type === 'number' && ['ne', 'gt', 'lt', 'gte', 'lte'].includes(filter.operator)) {
                                // This is a number range filter with operator
                                savedValues[fieldName] = {
                                    operator: filter.operator,
                                    comparisonValue: filter.value
                                };
                            } else {
                                savedValues[fieldName] = filter.value;
                            }
                        }
                    } else {
                        // Multiple filters - likely a date range
                        const dateRangeValue = {};
                        fieldFilters.forEach(filter => {
                            if (filter.operator === 'gte') {
                                dateRangeValue.from = filter.value;
                            } else if (filter.operator === 'lte') {
                                dateRangeValue.to = filter.value;
                            }
                        });
                        savedValues[fieldName] = dateRangeValue;
                    }
                });
                
                setFieldValues(savedValues);
                onApplyFilters(lastFilters, false); // Don't show loading for initial saved filter loading
            }
        };

        fetchLastFilters();
    }, [selectedCrmModule?._id]);  // eslint-disable-line

    const onFilterBtnClickedHandler = useCallback(async () => {
        const filters = buildFilters();
        // Only save to backend if not using local filters
        if (!useLocalFilters) {
            try {
                await saveModuleFilters(selectedCrmModule._id, filters);
            } catch (e) {
                console.log('Error to save filters', e)
            }
        }

        onApplyFilters?.(filters, true); // Show loading when actually applying filters
        onCancelClicked?.();
    }, [fieldValues, moduleSections, selectedCrmModule?._id, onApplyFilters, onCancelClicked, useLocalFilters]);

    const onClearFiltersClicked = async () => {
        // Only clear backend filters if not using local filters
        if (!useLocalFilters) {
            await clearModuleFilters(selectedCrmModule._id);
        }
        setFieldValues({});
        onApplyFilters([], true); // Show loading when clearing filters
        onCancelClicked?.()
    };

    const buildFilters = () => {
        const filters = [];

        // Helper function to check if a field has cascading children
        const hasCascadingChildren = (field) => {
            return field.type === 'select' && field.dropDownId?.dropDownItems?.some(item => item.children && item.children.length > 0);
        };

        // Helper function to collect all child field values for a cascading dropdown
        const collectCascadingValues = (baseFieldName) => {
            const cascadingValues = {};

            // Get parent field value
            const parentValue = fieldValues[baseFieldName];
            if (parentValue && Array.isArray(parentValue) && parentValue.length > 0) {
                cascadingValues.parent = parentValue;
            }

            // Collect child field values
            Object.keys(fieldValues).forEach(fieldName => {
                if (fieldName.startsWith(baseFieldName + '_child_')) {
                    const childValue = fieldValues[fieldName];
                    if (childValue && Array.isArray(childValue) && childValue.length > 0) {
                        cascadingValues[fieldName] = childValue;
                    }
                }
            });

            return cascadingValues;
        };

        moduleSections.forEach(section => {
            section.fields.forEach(field => {
                const value = fieldValues[field.name];

                // Handle cascading dropdown fields
                if (hasCascadingChildren(field)) {
                    console.log('Processing cascading dropdown field:', field.name);
                    const cascadingValues = collectCascadingValues(field.name);
                    console.log('Cascading values collected:', cascadingValues);

                    // Build full cascade filter with all selected levels
                    const cascadeFilter = {
                        field: field.name,
                        operator: 'cascading_match',
                        isCascadingDropdown: true,
                        cascadeValues: {}
                    };

                    // Add parent values if selected
                    if (cascadingValues.parent && cascadingValues.parent.length > 0) {
                        cascadeFilter.cascadeValues.parent = cascadingValues.parent;
                    }

                    // Add all child level values
                    const childKeys = Object.keys(cascadingValues).filter(key => key !== 'parent').sort();
                    childKeys.forEach(childKey => {
                        const childValues = cascadingValues[childKey];
                        if (childValues && childValues.length > 0) {
                            // Extract level number from key like "dfcsdefdsfsd_child_1"
                            const level = parseInt(childKey.split('_child_')[1]);
                            cascadeFilter.cascadeValues[`level_${level}`] = childValues;
                        }
                    });

                    // Only add filter if we have any values selected
                    if (Object.keys(cascadeFilter.cascadeValues).length > 0) {
                        filters.push(cascadeFilter);
                        console.log(`Added full cascade filter for ${field.name}:`, cascadeFilter.cascadeValues);
                    }

                    return; // Skip regular processing for cascading dropdowns
                }

                if (value !== undefined && value !== '') {
                    // Handle date range values for filtering
                    if ((field.type === 'date' || field.type === 'datetime') && 
                        typeof value === 'object' && (value.from || value.to)) {
                        
                        // Add "from" date filter (>=)
                        if (value.from) {
                            filters.push({
                                field: field.name,
                                operator: 'gte',
                                value: value.from
                            });
                        }
                        
                        // Add "to" date filter (<=)
                        if (value.to) {
                            filters.push({
                                field: field.name,
                                operator: 'lte',
                                value: value.to
                            });
                        }
                    } else if (field.type === 'number' && typeof value === 'object' && value.operator && value.comparisonValue !== '') {
                        // Handle number range filtering with operator
                        filters.push({
                            field: field.name,
                            operator: value.operator,
                            value: value.comparisonValue
                        });
                    } else {
                        // Handle regular field values
                        let operator = 'contains';
                        
                        // Use specific operators for different field types
                        if (field.type === 'number') {
                            operator = 'equals';
                        } else if (field.type === 'date' || field.type === 'datetime') {
                            operator = 'equals';
                        } else if (field.type === 'boolean') {
                            operator = 'equals';
                        } else if (field.type === 'select') {
                            // Handle multi-select values in filtering mode
                            if (Array.isArray(value) && value.length > 0) {
                                operator = 'in'; // Use 'in' operator for multiple values
                            } else if (!Array.isArray(value)) {
                                operator = 'equals'; // Single value selection
                            }
                        }
                        
                        // Only add filter if value is meaningful
                        if (field.type === 'select' && Array.isArray(value)) {
                            if (value.length > 0) {
                                filters.push({
                                    field: field.name,
                                    operator: operator,
                                    value: value
                                });
                            }
                        } else {
                            filters.push({
                                field: field.name,
                                operator: operator,
                                value: value
                            });
                        }
                    }
                }
            });
        });
        return filters;
    };

    // const onFilterBtnClickedHandler = useCallback(() => {
    //     const filters = buildFilters();
    //     if (typeof onApplyFilters === 'function') {
    //         onApplyFilters(filters);
    //         onCancelClicked()
    //     } else {
    //         console.log('Filters:', filters);
    //     }
    // }, [fieldValues, moduleSections]);

    // const getModuleRelationField = useCallback((moduleSections, linkModuleId) => {
    //     if (linkModuleId && moduleSections) {
    //         for (const section of moduleSections) {
    //             for (const field of section.fields) {
    //                 if (field.refModelId?._id === linkModuleId) {
    //                     return field
    //                 }
    //             }
    //         }
    //     }
    // }, [])

    const onLinkClickedHandler = useCallback((selectedRows, fieldName) => {
        // debugger
        // const linkModuleId = selectedRows?.[0]?.moduleId
        if (selectedRows?.[0] && fieldName) {
            const updateField = {
                name: fieldName,
                value: selectedRows?.[0]
            }

            handleChange({
                target: updateField
            })
        }
        // const relatedModuleField = getModuleRelationField(moduleSections, linkModuleId)
        // if (relatedModuleField) {
        //     const updateField = {
        //         name: relatedModuleField.name,
        //         value: selectedRows?.[0]
        //     }

        //     handleChange({
        //         target: updateField
        //     })
        // }
        // console.log(selectedRows)
    }, [moduleSections])

    return (
        <Box
            component="form"
            onSubmit={(e) => e.preventDefault()}
            noValidate
            sx={{
                p: 0,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    p: 1,
                    pr: 2 // Optional: to prevent scroll bar from overlapping content.
                }}
            >
                <ModuleSingleRecordSections
                    moduleSections={moduleSections}
                    onEditSectionField={() => { }}
                    fieldValues={fieldValues}
                    onFieldChange={handleChange}
                    alwaysOpenToEdit={true}
                    onLinkClicked={onLinkClickedHandler}
                    onSelectConv={onSelectConv}
                    OnAddToDialListClicked={OnAddToDialListClicked}
                    supportRequired={false}
                    isFilteringMode={true}
                />
            </Box>
            <CrmDivider sx={{ my: 2 }} />

            {/* Footer Buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2} sx={{ pr: 2 }}>
                {/* <CrmButton variant="contained" color="primary" onClick={onFilterBtnClickedHandler}>
                    {t('Filter it')}
                </CrmButton> */}
                <CrmButton color="crmContainedGray94" onClick={onFilterBtnClickedHandler}>
                    {t('Filter it')}
                </CrmButton>
                {/* <CrmButton variant="outlined" color="primary" onClick={onCancelClicked}>
                    {t('Cancel')}
                </CrmButton> */}
                <CrmButton color="crmOutlinedGray94" onClick={onCancelClicked}>
                    {t('Cancel')}
                </CrmButton>
            </Box>
        </Box>
    )
}

export default ModuleFilterRecords